import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, ExternalLink, Usb } from 'lucide-react';
import { PxWindow } from './PixelUI';
import MiniByte from './MiniByte';
import { devWarn } from '../lib/log';
import { getWire, getStats } from '../api';
import { TOKEN_INFO, BYTE_SUGGESTIONS, fmtUsd, fmtBig, fmtCount } from '../catalog';

const NoSignal = () => <span className="nv-unread">no signal</span>;
const Dot = () => <span className="nv-unread">·</span>;

const PctCell = ({ value }) => {
  if (value === null || value === undefined) return <Dot />;
  const cls = value >= 0 ? 'nv-up' : 'nv-down';
  return <span className={cls}>{value >= 0 ? '+' : ''}{value.toFixed(2)}%</span>;
};

const TickerChange = ({ value }) => {
  if (value === null || value === undefined) {
    return <span style={{ opacity: 0.4, fontStyle: 'italic' }}>no signal</span>;
  }
  return (
    <span style={{ color: value >= 0 ? 'var(--nv-green)' : 'var(--nv-red)' }}>
      {value >= 0 ? '▲' : '▼'} {Math.abs(value).toFixed(2)}%
    </span>
  );
};

/* ---------------- ticker strip (live wire) ---------------- */
export const TickerStrip = () => {
  const [coins, setCoins] = useState(null);
  useEffect(() => { getWire().then(setCoins); }, []);

  if (!coins) {
    return (
      <div className="nv-ticker" data-testid="ticker-strip">
        <div style={{ padding: '12px 20px', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--nv-brown)' }}>
          the wire is warming up…
        </div>
      </div>
    );
  }
  const base = coins.length
    ? Array.from({ length: Math.max(12, coins.length) }, (_, i) => coins[i % coins.length])
    : [];
  const items = [...base, ...base];
  return (
    <div className="nv-ticker" data-testid="ticker-strip">
      <div className="nv-ticker-track">
        {items.map((c, i) => (
          <span key={`${c.id}-${i}`} className="nv-ticker-item">
            <span className="font-pixel" style={{ fontSize: 9 }}>{c.symbol}</span>
            <span>{fmtUsd(c.price) ?? 'no signal'}</span>
            <TickerChange value={c.change24h} />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------- system monitor (live chain via RPC) ---------------- */
const statCells = (stats) => [
  { label: 'Blocks sealed', value: stats ? fmtCount(stats.total_blocks) : null },
  { label: 'Block time', value: stats && stats.block_time_s !== null ? `${stats.block_time_s.toFixed(2)} s` : null },
  { label: 'Gas average', value: stats && stats.gas_avg_gwei !== null ? `${stats.gas_avg_gwei} gwei` : null },
  { label: 'ETH quote', value: stats && stats.eth_usd !== null ? `$${Math.round(stats.eth_usd).toLocaleString()}` : null },
  { label: 'Chain id', value: '4663' },
  { label: 'Ground', value: 'Robinhood' },
];

const StatValue = ({ loading, value }) => {
  if (loading) return <>…</>;
  return value ?? <NoSignal />;
};

export const SystemMonitor = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { getStats().then(setStats); }, []);

  return (
    <section className="nv-section nv-stars on-night" id="status">
      <div className="nv-section-inner" style={{ position: 'relative', zIndex: 1 }}>
        <p className="nv-eyebrow">no hands on these dials — they read themselves</p>
        <h2 className="nv-h2" data-testid="system-monitor-title">SYSTEM MONITOR</h2>
        <p style={{ color: 'var(--nv-tan)', maxWidth: 620, marginBottom: 30, lineHeight: 1.7 }}>
          Blocks and gas come straight off the chain's own RPC, the ETH quote off the
          ledger. Whatever a feed refuses to send prints as no signal — this desk never
          dresses silence up as a number.
        </p>
        <div className="nv-stat-grid" data-testid="stat-grid">
          {statCells(stats).map((s) => (
            <div className="nv-stat-cell" key={s.label}>
              <div className="k">{s.label}</div>
              <div className="v"><StatValue loading={stats === null} value={s.value} /></div>
            </div>
          ))}
        </div>
        <div className="nv-witness-chips">
          {['THE WIRE', 'THE LEDGER', 'THE DOCK', 'BYTE'].map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- wallet dock ---------------- */
const DockPanel = ({ account, hasWallet, connecting, onConnect, onEject }) => {
  if (account) {
    return (
      <>
        <p className="font-pixel" style={{ fontSize: 11, color: 'var(--nv-green)', marginBottom: 16 }}>WALLET DOCKED</p>
        <p style={{ fontSize: 13, wordBreak: 'break-all', marginBottom: 18 }}>{account}</p>
        <button className="px-btn sm ghost" onClick={onEject} data-testid="undock-btn">Undock</button>
      </>
    );
  }
  if (hasWallet) {
    return (
      <>
        <p className="font-pixel" style={{ fontSize: 11, marginBottom: 18 }}>DOCK EMPTY · WALLET FOUND</p>
        <button className="px-btn" onClick={onConnect} disabled={connecting} data-testid="dock-wallet-btn">
          <Usb size={14} /> {connecting ? 'Docking…' : 'Dock Wallet'}
        </button>
      </>
    );
  }
  return (
    <>
      <p className="font-pixel" style={{ fontSize: 11, color: 'var(--nv-red)', marginBottom: 18 }}>NO WALLET DOCKED</p>
      <p style={{ fontSize: 13, lineHeight: 1.7, maxWidth: 380, margin: '0 auto' }}>
        The dock reads any injected wallet — MetaMask, Rabby, anything EIP-1193.
        Install one, reload, and the bay will light up.
      </p>
    </>
  );
};

export const WalletDock = () => {
  const [account, setAccount] = useState(null);
  const [hasWallet, setHasWallet] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    setHasWallet(typeof window !== 'undefined' && Boolean(window.ethereum));
  }, []);

  const connect = async () => {
    if (!window.ethereum) return;
    try {
      setConnecting(true);
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setAccount(accounts[0]);
      toast.success('Wallet docked — the desk can read your line');
    } catch (err) {
      devWarn('[nanovram] wallet dock refused:', err?.message);
      toast.error('The wallet refused the dock');
    } finally {
      setConnecting(false);
    }
  };

  const eject = () => { setAccount(null); toast('Wallet undocked — the bay is dark'); };

  return (
    <section className="nv-section bg-cream-band on-cream" id="dock">
      <div className="nv-section-inner">
        <p className="nv-eyebrow">read-only by design — the desk can look, never touch</p>
        <h2 className="nv-h2" data-testid="wallet-dock-title">WALLET DOCK</h2>
        <p style={{ maxWidth: 640, marginBottom: 34, lineHeight: 1.7 }}>
          Slide your wallet into the dock and the desk reads your balances over your own
          keys. It stores nothing, signs nothing on its own, and the moment you undock
          the bay goes dark.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 26 }}>
          <PxWindow title="DOCK.CFG" tape>
            <div style={{ padding: 18 }}>
              <div className="nv-kv"><span className="k">Port</span><span className="v">wallet bay, 60 pin</span></div>
              <div className="nv-kv"><span className="k">Card</span><span className="v">your own wallet</span></div>
              <div className="nv-kv"><span className="k">Line</span><span className="v">ten blocks a second</span></div>
              <div className="nv-kv"><span className="k">Custody</span><span className="v">never ours</span></div>
            </div>
          </PxWindow>
          <PxWindow title="WALLET DOCK" tape>
            <div style={{ padding: 24, textAlign: 'center' }} data-testid="wallet-dock-panel">
              <DockPanel account={account} hasWallet={hasWallet} connecting={connecting} onConnect={connect} onEject={eject} />
            </div>
          </PxWindow>
        </div>
        <p style={{ marginTop: 26, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--nv-brown)' }}>
          The desk keeps no keys and runs no node of its own · no wallet means no reads — and it says so plainly
        </p>
      </div>
    </section>
  );
};

/* ---------------- wire preview (live) ---------------- */
const WirePreviewBody = ({ coins }) => {
  if (coins === undefined) {
    return <p style={{ padding: '10px 18px 20px', color: 'rgba(247,240,225,.5)', fontFamily: 'VT323, monospace', fontSize: 20 }}>tuning the wire…</p>;
  }
  if (coins === null) {
    return (
      <p style={{ padding: '10px 18px 20px', color: 'rgba(247,240,225,.6)', fontFamily: 'VT323, monospace', fontSize: 20 }}>
        the wire dropped this window — give it a minute
      </p>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="nv-tape-table" data-testid="wire-preview-table">
        <thead>
          <tr><th>#</th><th>Coin</th><th>Price</th><th>24h</th><th>Volume 24h</th></tr>
        </thead>
        <tbody>
          {coins.slice(0, 8).map((c, i) => (
            <tr key={c.id} className="row">
              <td style={{ color: 'rgba(247,240,225,.45)' }}>{i + 1}</td>
              <td><span className="nv-sym">{c.symbol}</span> <span style={{ opacity: 0.6, marginLeft: 8 }}>{c.name}</span></td>
              <td>{fmtUsd(c.price) ?? <Dot />}</td>
              <td><PctCell value={c.change24h} /></td>
              <td>{fmtBig(c.volume24h) ?? <Dot />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const WirePreview = () => {
  const [coins, setCoins] = useState(undefined);
  useEffect(() => { getWire().then(setCoins); }, []);

  return (
    <section className="nv-section nv-stars on-night" id="wire">
      <div className="nv-section-inner" style={{ position: 'relative', zIndex: 1 }}>
        <p className="nv-eyebrow">the live ETH quote, straight from the ledger · one-minute cache</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 30 }}>
          <h2 className="nv-h2" style={{ margin: 0 }} data-testid="wire-title">THE WIRE</h2>
          <Link to="/wire" className="px-btn sm" data-testid="open-full-wire-btn">Open the Wire</Link>
        </div>
        <PxWindow title="WIRE.ROM" tape className="nv-tape-term nv-scan">
          <p className="nv-tape-prompt">NV8&gt; mount wire.rom --eth<span className="nv-cursor" /></p>
          <WirePreviewBody coins={coins} />
          <p style={{ padding: '12px 18px', fontSize: 11, color: 'rgba(247,240,225,.45)', letterSpacing: 1 }}>
            · a dot means the feed sent no signal — silence never prints as zero
          </p>
        </PxWindow>
      </div>
    </section>
  );
};

/* ---------------- byte preview ---------------- */
export const BytePreview = () => (
  <section className="nv-section bg-ground-band on-cream" id="byte">
    <div className="nv-section-inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 34, alignItems: 'center' }}>
      <div>
        <p className="nv-eyebrow" style={{ color: 'var(--nv-cream)' }}>the desk's helper chip — a real model, fed the live wire first</p>
        <h2 className="nv-h2" style={{ color: 'var(--nv-cream)' }} data-testid="byte-title">ASK BYTE</h2>
        <p style={{ color: 'var(--nv-cream-2)', lineHeight: 1.75, marginBottom: 20 }}>
          Byte runs on a real model with a real key. Before every answer it gets the live
          wire pressed into its hands — those are the only figures it may repeat. Ask for
          something the wire doesn't carry and it tells you exactly that, instead of
          inventing a plausible number. When the wire is down, Byte admits that too.
        </p>
        <div className="nv-sugg">
          {BYTE_SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => { window.location.href = `/agent?q=${encodeURIComponent(s)}`; }}>{s}</button>
          ))}
        </div>
        <Link to="/agent" className="px-btn" data-testid="open-byte-btn">Open Byte</Link>
      </div>
      <MiniByte />
    </div>
  </section>
);

/* ---------------- mint id ---------------- */
export const MintId = () => {
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(TOKEN_INFO.cartId);
      toast.success('Mint id copied to clipboard');
    } catch {
      toast.error('Could not copy — copy it manually');
    }
  };
  return (
    <section className="nv-section nv-stars on-night" id="id">
      <div className="nv-section-inner" style={{ maxWidth: 760, position: 'relative', zIndex: 1 }}>
        <p className="nv-eyebrow">one mint, one desk — verify before you believe</p>
        <h2 className="nv-h2" data-testid="mint-id-title">MINT ID</h2>
        <PxWindow title="MINT.ID" tape>
          <div style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              <span className="font-pixel" style={{ fontSize: 18 }}>{TOKEN_INFO.ticker}</span>
              <span className="font-pixel" style={{ fontSize: 9, color: 'var(--nv-green)' }}>MINTED · LIVE</span>
            </div>
            <div className="nv-kv"><span className="k">Product</span><span className="v">{TOKEN_INFO.product}</span></div>
            <div className="nv-kv"><span className="k">Ticker</span><span className="v">{TOKEN_INFO.ticker}</span></div>
            <div className="nv-kv"><span className="k">Ground</span><span className="v">{TOKEN_INFO.ground}</span></div>
            <div className="nv-kv"><span className="k">Mint id</span><span className="v" style={{ fontSize: 12 }}>{TOKEN_INFO.cartId}</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 20 }}>
              <button className="px-btn sm" onClick={copyKey} data-testid="copy-key-btn"><Copy size={13} /> Copy Key</button>
              <a className="px-btn sm ghost" href={`https://robinhoodchain.blockscout.com/address/${TOKEN_INFO.cartId}`} target="_blank" rel="noreferrer" data-testid="ledger-btn">
                <ExternalLink size={13} /> Ledger
              </a>
            </div>
          </div>
        </PxWindow>
      </div>
    </section>
  );
};
